"use client"

import { useId, useState } from "react"
import type { MilestoneFormRequirement } from "@/schema/task"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"

import { ApiRequestError } from "@/hooks/api/base"
import { AppDialogContent } from "@/components/dialogs/app-dialog-content"

import { findMissingRequiredFields } from "./form-field-response-helpers"
import { MilestoneFormFieldsInput } from "./milestone-form-fields-input"

type FormFieldsRequirementIntent = "complete" | "move"

const INTENT_COPY = {
  complete: {
    title: (milestoneTitle: string) => `Complete ${milestoneTitle}`,
    action: "Complete milestone",
    description: "Complete the required form fields for the next milestone.",
  },
  move: {
    title: (milestoneTitle: string) => `Move to ${milestoneTitle}`,
    action: "Move project",
    description: "Answer this milestone's questions to move the project.",
  },
} satisfies Record<FormFieldsRequirementIntent, unknown>

interface FormFieldsRequirementDialogContentProps {
  requirement: MilestoneFormRequirement
  onConfirm: (formResponses: Record<string, string | number | boolean | null>) => Promise<void> | void
  intent: FormFieldsRequirementIntent
}

function FormFieldsRequirementDialogContent({
  requirement,
  onConfirm,
  intent,
}: FormFieldsRequirementDialogContentProps) {
  const copy = INTENT_COPY[intent]
  const closeDialog = useGlobalDialogStore((state) => state.popDialog)
  const formId = useId()
  const [formResponses, setFormResponses] = useState<Record<string, string | number | boolean | null>>(() => {
    const initial: Record<string, string | number | boolean | null> = {}
    for (const field of requirement.fields) {
      if (field.current_value == null) {
        if (field.has_response) initial[field.id] = null
        continue
      }
      if (typeof field.current_value === "object" && "storage_file_id" in field.current_value) {
        initial[field.id] = field.current_value.storage_file_id
      } else {
        initial[field.id] = field.current_value
      }
    }
    return initial
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    if (isSubmitting) return

    if (findMissingRequiredFields(requirement.fields, formResponses, requirement.household_id ?? null).length > 0)
      return

    setIsSubmitting(true)
    try {
      await onConfirm(formResponses)
      closeDialog()
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasAllRequired =
    findMissingRequiredFields(requirement.fields, formResponses, requirement.household_id ?? null).length === 0

  return (
    <AppDialogContent
      variant="gridForm"
      dismiss={isSubmitting ? "none" : "all"}
      title={copy.title(requirement.milestone_title)}
      action={{ label: copy.action, form: formId, disabled: !hasAllRequired, pending: isSubmitting }}
      cancel={{ disabled: isSubmitting }}
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          void handleConfirm()
        }}
      >
        <p className="text-muted-foreground text-sm">{copy.description}</p>
        <MilestoneFormFieldsInput
          fields={requirement.fields.map((field) => ({
            ...field,
            current_file:
              field.field_type === "file_attachment" &&
              field.current_value &&
              typeof field.current_value === "object" &&
              "storage_file_id" in field.current_value
                ? field.current_value
                : null,
          }))}
          values={formResponses}
          onChange={setFormResponses}
          disabled={isSubmitting}
          householdRecordScope={{ householdId: requirement.household_id ?? null }}
          fileAttachmentScope={{
            householdId: requirement.household_id ?? null,
            projectTemplateId: requirement.project_template_id ?? null,
            hasStorageFolder: requirement.household_has_storage_folder ?? false,
          }}
        />
      </form>
    </AppDialogContent>
  )
}

interface OpenFormFieldsRequirementDialogOptions {
  requirement: MilestoneFormRequirement
  onConfirm: (formResponses: Record<string, string | number | boolean | null>) => Promise<void> | void
  intent?: FormFieldsRequirementIntent
}

export function openFormFieldsRequirementDialog({
  requirement,
  onConfirm,
  intent = "complete",
}: OpenFormFieldsRequirementDialogOptions) {
  useGlobalDialogStore.getState().openDialog({
    variant: "gridForm",
    content: <FormFieldsRequirementDialogContent requirement={requirement} onConfirm={onConfirm} intent={intent} />,
  })
}

export function getFormFieldsRequirementFromError(error: unknown): MilestoneFormRequirement | null {
  if (!(error instanceof ApiRequestError) || error.status !== 422) {
    return null
  }

  const errorBody = error.responseBody as
    | { error_code?: string; milestone_form_requirement?: MilestoneFormRequirement }
    | undefined
  if (!errorBody || errorBody.error_code !== "form_fields_required") {
    return null
  }

  return errorBody.milestone_form_requirement || null
}
